import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import WorkIcon from '@mui/icons-material/Work';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { useLanguage } from '../../contexts/LanguageContext';
import type { DashboardStats } from '../../types/api.types';
import { getDashboardStats } from '../../services/storage';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactElement;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Card
    sx={{
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        bgcolor: color,
      },
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: `${color}15` }}>
          {React.cloneElement(
            icon as React.ReactElement<{ sx: { fontSize: number; color: string } }>,
            { sx: { fontSize: 40, color } }
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard: React.FC = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState<DashboardStats>({ services: 0, kooperation: 0, totalContacts: 0, newContacts: 0 });

  useEffect(() => {
    getDashboardStats().then(setStats).catch(() => {});
  }, []);

  const statCards = [
    { key: 'services', title: t('admin.stats.services'), value: stats.services, icon: <BusinessCenterIcon />, color: '#2E8BC0' },
    { key: 'kooperation', title: t('admin.stats.kooperation'), value: stats.kooperation, icon: <WorkIcon />, color: '#555555' },
    { key: 'contacts', title: t('admin.stats.contacts'), value: stats.totalContacts, icon: <ContactMailIcon />, color: '#000000' },
    { key: 'new', title: t('admin.stats.new_contacts'), value: stats.newContacts, icon: <FiberNewIcon />, color: '#e74c3c' },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
        {t('admin.dashboard')}
      </Typography>

      <Grid container spacing={3}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.key}>
            <StatCard
              title={card.title}
              value={card.value}
              icon={card.icon}
              color={card.color}
            />
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mt: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Willkommen im Admin Dashboard!
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
            Hier kannst du alle Inhalte deiner Website verwalten:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            {[
              { label: 'Services', desc: 'Verwalte deine angebotenen Dienstleistungen' },
              { label: 'Kooperation', desc: 'Verwalte deine Kooperationspartner' },
              { label: 'Kontaktanfragen', desc: 'Bearbeite eingehende Anfragen' },
            ].map((item) => (
              <Typography key={item.label} component="li" sx={{ mb: 1 }}>
                <strong>{item.label}:</strong> {item.desc}
              </Typography>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminDashboard;
